import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Robot, Plus, Trash, PencilSimple, Chats, Headset, CheckCircle, PaperPlaneTilt, User as UserIcon, Warning,
} from "@phosphor-icons/react";

const emptyKb = () => ({ kind: "faq", title: "", question: "", answer: "", keywords: [], priority: 0, is_active: true });

export default function ChatbotAdmin({ embedded = false }) {
  const [tab, setTab] = useState("sessions");
  const [kb, setKb] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({ pending_contacts: 0, total_sessions: 0, total_messages: 0 });
  const [pendingOnly, setPendingOnly] = useState(false);
  const [selected, setSelected] = useState(null); // session detail
  const [replyBody, setReplyBody] = useState("");
  const [replying, setReplying] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyKb());
  const [editingId, setEditingId] = useState(null);
  const [deleteKb, setDeleteKb] = useState(null);
  const scrollRef = useRef();

  const loadStats = () => api.get("/admin/chatbot/stats").then(r => setStats(r.data));
  const loadKb = () => api.get("/admin/chatbot/knowledge").then(r => setKb(r.data || []));
  const loadSessions = () => api.get(`/admin/chatbot/sessions${pendingOnly ? "?only_pending=true" : ""}`).then(r => setSessions(r.data || []));
  const loadSelected = (sid) => api.get(`/admin/chatbot/sessions/${sid}`).then(r => { setSelected(r.data); scrollBottom(); loadStats(); loadSessions(); });

  const scrollBottom = () => setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }), 60);

  useEffect(() => { loadStats(); loadKb(); }, []);
  useEffect(() => { loadSessions(); }, [pendingOnly]);
  useEffect(() => { const t = setInterval(() => { loadStats(); loadSessions(); if (selected?.session?._id) loadSelected(selected.session._id); }, 20000); return () => clearInterval(t); }, [selected?.session?._id, pendingOnly]);

  const openNewKb = () => { setEditForm(emptyKb()); setEditingId(null); setDlgOpen(true); };
  const openEditKb = (k) => { setEditForm({ ...k, keywords: k.keywords || [] }); setEditingId(k._id); setDlgOpen(true); };
  const saveKb = async () => {
    const payload = { ...editForm, keywords: (editForm.keywords || []).filter(Boolean), priority: Number(editForm.priority) || 0 };
    if (!payload.answer.trim()) { toast.error("Risposta obbligatoria"); return; }
    try {
      if (editingId) await api.patch(`/admin/chatbot/knowledge/${editingId}`, payload);
      else await api.post("/admin/chatbot/knowledge", payload);
      toast.success("Salvato");
      setDlgOpen(false); loadKb();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const confirmDeleteKb = async () => {
    if (!deleteKb) return;
    try {
      await api.delete(`/admin/chatbot/knowledge/${deleteKb._id}`);
      toast.success("Eliminato"); setDeleteKb(null); loadKb();
    } catch (err) { toast.error("Errore"); }
  };

  const sendReply = async () => {
    if (!replyBody.trim() || !selected) return;
    setReplying(true);
    try {
      await api.post(`/admin/chatbot/sessions/${selected.session._id}/reply`, { body: replyBody });
      setReplyBody("");
      loadSelected(selected.session._id);
    } catch (err) { toast.error("Errore"); }
    finally { setReplying(false); }
  };
  const closeSession = async () => {
    if (!selected) return;
    try {
      await api.post(`/admin/chatbot/sessions/${selected.session._id}/close`);
      toast.success("Conversazione chiusa");
      loadSelected(selected.session._id);
    } catch (err) { toast.error("Errore"); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        {!embedded && (
          <div>
            <div className="label-eyebrow">Admin · Assistente virtuale</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-2 flex items-center gap-2">
              <Robot size={32} weight="duotone" color="#6D28D9" /> Chatbot pubblico
            </h1>
            <p className="text-muted-foreground mt-2">Gestisci la knowledge del bot e rispondi alle conversazioni degli utenti pubblici.</p>
          </div>
        )}
        <div className="flex gap-3">
          <div className="kpi-card p-3 min-w-[120px] text-center">
            <div className="text-[10px] uppercase text-muted-foreground">Da attenzionare</div>
            <div className={`font-display text-2xl font-bold ${stats.pending_contacts > 0 ? "text-red-600 animate-pulse" : "text-muted-foreground"}`} data-testid="stat-pending">
              {stats.pending_contacts}
            </div>
          </div>
          <div className="kpi-card p-3 min-w-[120px] text-center">
            <div className="text-[10px] uppercase text-muted-foreground">Sessioni tot.</div>
            <div className="font-display text-2xl font-bold text-purple-700">{stats.total_sessions}</div>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="sessions" data-testid="tab-sessions">
            <Chats size={14} className="mr-1" /> Conversazioni
            {stats.pending_contacts > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-red-600 text-white text-[10px] rounded-full">{stats.pending_contacts}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="kb" data-testid="tab-kb">Argomenti (KB)</TabsTrigger>
        </TabsList>

        <TabsContent value="sessions">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="kpi-card p-2 md:col-span-1 max-h-[75vh] overflow-y-auto">
              <div className="flex items-center gap-2 p-2 border-b">
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={pendingOnly} onChange={e => setPendingOnly(e.target.checked)} data-testid="filter-pending" />
                  Solo da attenzionare
                </label>
              </div>
              {sessions.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">Nessuna sessione.</div>
              ) : sessions.map(s => (
                <button key={s._id} onClick={() => loadSelected(s._id)}
                  className={`w-full text-left p-2 border-b hover:bg-purple-50 flex items-start gap-2 ${selected?.session?._id === s._id ? "bg-purple-50" : ""}`}
                  data-testid={`session-item-${s._id}`}>
                  <div className="w-8 h-8 rounded-full ht-gradient flex items-center justify-center text-white flex-shrink-0">
                    {s.requested_contact ? <Headset size={14} weight="duotone" /> : <Robot size={14} weight="duotone" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <div className="text-sm font-medium truncate">{s.visitor_name || "Visitatore anonimo"}</div>
                      {s.unread_for_admin > 0 && (
                        <span className="bg-red-600 text-white text-[10px] rounded-full px-1.5 py-0.5">{s.unread_for_admin}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">{s.visitor_email || s.page_source || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">{fmtDate(s.last_message_at)}</div>
                    <div className="flex gap-1 mt-1">
                      {s.requested_contact && !s.resolved_at && <span className="text-[9px] px-1 rounded bg-amber-100 text-amber-800">In attesa</span>}
                      {s.resolved_at && <span className="text-[9px] px-1 rounded bg-emerald-100 text-emerald-800">Chiusa</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="kpi-card md:col-span-2 flex flex-col max-h-[75vh]">
              {!selected ? (
                <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground py-16">
                  Seleziona una conversazione a sinistra.
                </div>
              ) : (
                <>
                  <div className="p-3 border-b">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-sm">{selected.session.visitor_name || "Visitatore anonimo"}</div>
                        <div className="text-xs text-muted-foreground">{selected.session.visitor_email || "—"} · {selected.session.visitor_phone || "—"}</div>
                        <div className="text-[10px] text-muted-foreground">Da pagina: {selected.session.page_source || "—"}</div>
                      </div>
                      {!selected.session.resolved_at && (
                        <Button size="sm" variant="outline" onClick={closeSession} data-testid="close-session-btn">
                          <CheckCircle size={13} className="mr-1" /> Chiudi
                        </Button>
                      )}
                    </div>
                    {selected.session.summary && (
                      <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs">
                        <div className="font-semibold text-amber-900 flex items-center gap-1 mb-1">
                          <Warning size={12} weight="fill" /> Riepilogo del bot
                        </div>
                        <div className="text-amber-900 whitespace-pre-wrap">{selected.session.summary}</div>
                      </div>
                    )}
                  </div>
                  <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-[color:hsl(var(--surface-alt))]">
                    {(selected.messages || []).map(m => {
                      const isBot = m.role === "assistant";
                      const isAdmin = m.role === "admin";
                      return (
                        <div key={m._id} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                          <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
                            m.role === "user" ? "bg-white border" :
                            isAdmin ? "ht-gradient text-white" :
                            "bg-purple-100 text-purple-900 border border-purple-200"
                          }`}>
                            <div className={`flex items-center gap-1 text-[10px] font-semibold mb-0.5 ${
                              m.role === "user" ? "text-muted-foreground" :
                              isAdmin ? "text-white/85" : "text-purple-700"
                            }`}>
                              {m.role === "user" ? <><UserIcon size={10} /> Visitatore</> :
                               isAdmin ? <><Headset size={10} /> Tu</> :
                               <><Robot size={10} /> Bot</>}
                            </div>
                            {m.body}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="p-3 border-t bg-white flex gap-2">
                    <Textarea rows={2} placeholder="Rispondi come operatore…" value={replyBody}
                      onChange={e => setReplyBody(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      data-testid="admin-chat-input" />
                    <Button onClick={sendReply} disabled={replying || !replyBody.trim()} className="ht-gradient border-0" data-testid="admin-chat-send">
                      <PaperPlaneTilt size={16} weight="bold" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="kb">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">
              La knowledge del chatbot pubblico è composta esclusivamente dagli argomenti che aggiungi qui.
              Il bot risponderà SOLO usando queste spiegazioni e non fornirà mai prezzi.
              Per ogni argomento fornisci un titolo e una spiegazione chiara.
            </p>
            <div className="mt-3">
              <Button onClick={openNewKb} className="ht-gradient border-0" data-testid="new-kb-btn">
                <Plus size={14} className="mr-1" weight="bold" /> Nuovo argomento
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {kb.length === 0 ? (
              <div className="kpi-card text-center py-10 text-sm text-muted-foreground">
                Nessun argomento caricato. Aggiungine uno per istruire il chatbot.
              </div>
            ) : kb.map(k => (
              <div key={k._id} className="kpi-card flex items-start gap-3 p-3">
                <div className={`text-[10px] uppercase px-2 py-0.5 rounded ${k.kind === "faq" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"}`}>
                  {k.kind === "faq" ? "domanda" : "argomento"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{k.title || (k.kind === "faq" ? k.question : "Argomento")}</div>
                  {k.question && k.title !== k.question && <div className="text-xs text-muted-foreground">D: {k.question}</div>}
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{k.answer}</div>
                  {k.keywords?.length > 0 && (
                    <div className="flex gap-1 flex-wrap mt-1">
                      {k.keywords.map((w, i) => <span key={`${w}-${i}`} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{w}</span>)}
                    </div>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  <div>P{k.priority}</div>
                  {!k.is_active && <div className="text-red-600">off</div>}
                </div>
                <div className="flex flex-col gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEditKb(k)} data-testid={`edit-kb-${k._id}`}><PencilSimple size={14} /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDeleteKb(k)} data-testid={`delete-kb-${k._id}`}><Trash size={14} /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog: KB new/edit */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? "Modifica argomento" : "Nuovo argomento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo</Label>
              <select value={editForm.kind} onChange={e => setEditForm({...editForm, kind: e.target.value})}
                className="w-full h-10 border rounded-md px-2 text-sm" data-testid="kb-kind">
                <option value="faq">Domanda &amp; risposta</option>
                <option value="text">Argomento libero (spiegazione)</option>
              </select>
            </div>
            <div><Label>Titolo argomento</Label>
              <Input value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})}
                placeholder="es. Come funziona Housetag" data-testid="kb-title" />
            </div>
            {editForm.kind === "faq" && (
              <div><Label>Domanda</Label>
                <Input value={editForm.question} onChange={e => setEditForm({...editForm, question: e.target.value})}
                  placeholder="Es. Come ci si registra?" data-testid="kb-question" />
              </div>
            )}
            <div><Label>Spiegazione *</Label>
              <Textarea rows={6} value={editForm.answer} onChange={e => setEditForm({...editForm, answer: e.target.value})}
                data-testid="kb-answer" required
                placeholder="Scrivi la spiegazione che il chatbot userà per rispondere. Non inserire prezzi o tariffe." />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Priorità (più alto = più rilevante)</Label>
                <Input type="number" value={editForm.priority} onChange={e => setEditForm({...editForm, priority: e.target.value})} />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editForm.is_active}
                    onChange={e => setEditForm({...editForm, is_active: e.target.checked})} />
                  Attivo
                </label>
              </div>
            </div>
            <div><Label>Parole chiave (opzionali, separate da virgola)</Label>
              <Input value={(editForm.keywords || []).join(", ")}
                onChange={e => setEditForm({...editForm, keywords: e.target.value.split(",").map(x => x.trim())})}
                placeholder="registrazione, iscrizione, account" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlgOpen(false)}>Annulla</Button>
            <Button onClick={saveKb} className="ht-gradient border-0" data-testid="kb-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: KB delete */}
      <Dialog open={!!deleteKb} onOpenChange={v => !v && setDeleteKb(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Eliminare il blocco?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground"><strong>{deleteKb?.title || deleteKb?.question}</strong> — verrà rimosso definitivamente.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteKb(null)}>Annulla</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDeleteKb} data-testid="kb-delete-confirm">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
